import React, { useCallback, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import ShareIcon from "@mui/icons-material/Share";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import SpeedIcon from "@mui/icons-material/Speed";
import TuneIcon from "@mui/icons-material/Tune";
import ClearIcon from "@mui/icons-material/Clear";
import PublicIcon from "@mui/icons-material/Public";
import { toast } from "react-toastify";
import { alpha } from "@mui/material/styles";
import TextInput from "../../../Components/Fields/TextInput.js";
import api from "../../../conf/api.js";
import TimeDurationPicker from "../../../Components/Fields/TimeDurationPicker.js";
import dayjs from "dayjs";
import { SocialPlatform } from "../Enums/enums.js";

const DAYS_OF_WEEK = [
  { value: 0, label: "Monday", short: "Mon" },
  { value: 1, label: "Tuesday", short: "Tue" },
  { value: 2, label: "Wednesday", short: "Wed" },
  { value: 3, label: "Thursday", short: "Thu" },
  { value: 4, label: "Friday", short: "Fri" },
  { value: 5, label: "Saturday", short: "Sat" },
  { value: 6, label: "Sunday", short: "Sun" },
];

const PLATFORM_OPTIONS = Object.values(SocialPlatform).map((v) => ({
  value: v,
  label: v.charAt(0) + v.slice(1).toLowerCase(),
}));

// Brand accent per platform — used as a colored dot in the platform select
const PLATFORM_COLORS = {
  YOUTUBE: "#FF0000",
  TIKTOK: "#010101",
  INSTAGRAM: "#E1306C",
  FACEBOOK: "#1877F2",
  PINTEREST: "#E60023",
  SNAPCHAT: "#B8B600",
  THREADS: "#1c1c1c",
  DAILYMOTION: "#0066DC",
  MSN: "#00A4EF",
  OTHERS: "#909090",
};

const INITIAL_FORM = {
  feed: null,
  platform: "",
  selected_days: [],
  time_start: null,
  time_end: null,
  time_start_2: null,
  time_end_2: null,
  uploading_gap: "",
  max_uploads_per_day: "",
  is_active: true,
};

const PlatformDot = ({ platform }) => (
  <Box
    component="span"
    sx={{
      width: 10,
      height: 10,
      borderRadius: "50%",
      bgcolor: PLATFORM_COLORS[platform] || PLATFORM_COLORS.OTHERS,
      display: "inline-block",
      flexShrink: 0,
    }}
  />
);

// Section wrapper — outlined card with an icon header, matching the
// Video Editor's panel styling.
function Section({ icon, title, subtitle, children, accent = "#4338ca" }) {
  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{ borderRadius: 2, overflow: "hidden", borderColor: "#e2e8f0", bgcolor: "white" }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          gap: 1,
          bgcolor: alpha(accent, 0.04),
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1.5,
            bgcolor: alpha(accent, 0.12),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: accent,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: accent, lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
}

export default function SocialFeedSchedulerBox({ open, scheduler, onClose }) {
  const isUpdate = Boolean(scheduler);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [feeds, setFeeds] = useState([]);

  // ── helpers ──────────────────────────────────────────────────────────────

  const convertStringToDuration = useCallback((_time) => {
    if (!_time) return null;
    const [hour, min, sec] = _time.split(":");
    return dayjs().hour(parseInt(hour)).minute(parseInt(min)).second(parseInt(sec || 0)).millisecond(0);
  }, []);

  const convertDurationToUTCString = useCallback((dayjsTime) => {
    if (!dayjsTime) return "";
    if (typeof dayjsTime === "string") return dayjsTime;
    if (!dayjs.isDayjs(dayjsTime)) return "";
    const pad = (n) => n.toString().padStart(2, "0");
    return `${pad(dayjsTime.hour())}:${pad(dayjsTime.minute())}:${pad(dayjsTime.second())}`;
  }, []);

  const isValidTimeRange = (from, to) => {
    const [fh, fm, fs] = from.split(":").map(Number);
    const [th, tm, ts] = to.split(":").map(Number);
    return fh * 3600 + fm * 60 + fs < th * 3600 + tm * 60 + ts;
  };

  // "2h 30m" label for a window whose bounds are both set and valid
  const windowLength = (start, end) => {
    if (!dayjs.isDayjs(start) || !dayjs.isDayjs(end)) return null;
    const mins = end.diff(start, "minute");
    if (mins <= 0) return null;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h${m > 0 ? ` ${m}m` : ""}` : `${m}m`;
  };

  const fmtHM = (t) => (dayjs.isDayjs(t) ? t.format("HH:mm") : null);

  // ── data loaders ─────────────────────────────────────────────────────────

  const fetchFeeds = useCallback(async () => {
    try {
      const res = await api.get("/mrss/feeds/", {
        params: { page: 1, page_size: 10000 },
      });
      setFeeds(res.data?.results || res.data || []);
    } catch {
      console.error("Error fetching feeds");
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchFeeds();
    if (scheduler) {
      setFormData({
        ...scheduler,
        feed: null, // resolved after feeds load
        time_start: convertStringToDuration(scheduler.time_start),
        time_end: convertStringToDuration(scheduler.time_end),
        time_start_2: convertStringToDuration(scheduler.time_start_2),
        time_end_2: convertStringToDuration(scheduler.time_end_2),
        uploading_gap: scheduler.uploading_gap ?? "",
        max_uploads_per_day: scheduler.max_uploads_per_day ?? "",
      });
    } else {
      setFormData(INITIAL_FORM);
    }
  }, [open, scheduler, convertStringToDuration, fetchFeeds]);

  // Resolve feed object once feeds list is loaded
  useEffect(() => {
    if (scheduler && feeds.length > 0) {
      const feed = feeds.find((f) => f.id === scheduler.feed);
      if (feed) setFormData((prev) => ({ ...prev, feed }));
    }
  }, [feeds, scheduler]);

  // ── handlers ─────────────────────────────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggle = (e) => {
    setFormData((prev) => ({ ...prev, is_active: e.target.checked }));
  };

  const toggleDay = (val) => {
    setFormData((prev) => ({
      ...prev,
      selected_days: prev.selected_days.includes(val)
        ? prev.selected_days.filter((d) => d !== val)
        : [...prev.selected_days, val],
    }));
  };

  const setDays = (days) =>
    setFormData((prev) => ({ ...prev, selected_days: days }));

  const handleTime = (field) => (value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const clearSecondWindow = () =>
    setFormData((prev) => ({ ...prev, time_start_2: null, time_end_2: null }));

  const handleErrorResponse = (error) => {
    const errorObj = error.response?.data;
    if (errorObj) {
      Object.entries(errorObj).forEach(([field, msgs]) => {
        (Array.isArray(msgs) ? msgs : [msgs]).forEach((m) =>
          toast.error(`${field}: ${m}`)
        );
      });
    } else {
      toast.error("Something went wrong");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const time_start = convertDurationToUTCString(formData.time_start);
    const time_end = convertDurationToUTCString(formData.time_end);
    const time_start_2 = convertDurationToUTCString(formData.time_start_2);
    const time_end_2 = convertDurationToUTCString(formData.time_end_2);

    if (!time_start || !time_end) {
      toast.error("Please set both start and end times.");
      return;
    }
    if (!isValidTimeRange(time_start, time_end)) {
      toast.error("Start time must be before end time.");
      return;
    }
    // Second window is optional, but must be complete and valid when used
    if (!!time_start_2 !== !!time_end_2) {
      toast.error("Set both start and end of the second window (or clear both).");
      return;
    }
    if (time_start_2 && !isValidTimeRange(time_start_2, time_end_2)) {
      toast.error("Second window start must be before its end.");
      return;
    }
    if (!formData.feed) {
      toast.error("Please select a feed.");
      return;
    }
    if (!formData.platform) {
      toast.error("Please select a platform.");
      return;
    }

    const payload = {
      feed: formData.feed.id,
      platform: formData.platform,
      selected_days: formData.selected_days,
      time_start,
      time_end,
      time_start_2: time_start_2 || null,
      time_end_2: time_end_2 || null,
      uploading_gap: formData.uploading_gap !== "" ? parseInt(formData.uploading_gap) : null,
      max_uploads_per_day:
        formData.max_uploads_per_day !== ""
          ? parseInt(formData.max_uploads_per_day)
          : null,
      is_active: formData.is_active,
    };

    try {
      if (isUpdate) {
        await api.patch(`/mrss/schedulers/${scheduler.id}/`, payload);
        toast.success("Social scheduler updated.");
      } else {
        await api.post("/mrss/schedulers/", payload);
        toast.success("Social scheduler created.");
      }
      onClose(true);
    } catch (error) {
      handleErrorResponse(error);
    }
  };

  // ── derived display values ────────────────────────────────────────────────

  const win1Len = windowLength(formData.time_start, formData.time_end);
  const win2Len = windowLength(formData.time_start_2, formData.time_end_2);
  const hasSecondWindow = formData.time_start_2 || formData.time_end_2;

  const scheduleSummary = (() => {
    const w1s = fmtHM(formData.time_start);
    const w1e = fmtHM(formData.time_end);
    if (!w1s || !w1e) return null;
    const w2s = fmtHM(formData.time_start_2);
    const w2e = fmtHM(formData.time_end_2);
    return w2s && w2e
      ? `Publishing allowed ${w1s}–${w1e} and ${w2s}–${w2e} UTC`
      : `Publishing allowed ${w1s}–${w1e} UTC`;
  })();

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={() => onClose(false)}
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* ── Gradient header ── */}
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)",
            color: "white",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* decorative circles */}
          <Box sx={{ position: "absolute", top: -40, right: -40, width: 150, height: 150, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />
          <Box sx={{ position: "absolute", bottom: -50, left: 90, width: 110, height: 110, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.03)", pointerEvents: "none" }} />

          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: "rgba(255,255,255,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <ScheduleIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                  {isUpdate ? "Update" : "Add"} Social Feed Scheduler
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                  Control when this feed's videos go out to a social platform
                </Typography>
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Switch checked={formData.is_active} onChange={handleToggle} color="success" />
              }
              label={
                <Typography variant="body2" fontWeight={600} sx={{ color: "white" }}>
                  {formData.is_active ? "Active" : "Inactive"}
                </Typography>
              }
              sx={{ mr: 0, flexShrink: 0 }}
            />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: "#f8fafc", p: 3 }}>
        <form onSubmit={handleSubmit} id="social-scheduler-form">
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {/* ── Target ── */}
            <Section
              icon={<ShareIcon sx={{ fontSize: 16 }} />}
              title="Target"
              subtitle="Which feed publishes, and to which platform"
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    value={formData.feed}
                    options={feeds}
                    getOptionLabel={(o) => o.name || ""}
                    size="small"
                    fullWidth
                    readOnly={isUpdate}
                    onChange={(_, val) =>
                      setFormData((prev) => ({ ...prev, feed: val }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Feed"
                        helperText={
                          isUpdate
                            ? "Feed cannot be changed after creation"
                            : "The MRSS feed whose videos will be published"
                        }
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <RssFeedIcon fontSize="small" color="action" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Platform</InputLabel>
                    <Select
                      label="Platform"
                      name="platform"
                      value={formData.platform}
                      onChange={handleChange}
                      disabled={isUpdate}
                      renderValue={(v) => (
                        <Box display="flex" alignItems="center" gap={1}>
                          <PlatformDot platform={v} />
                          {v.charAt(0) + v.slice(1).toLowerCase()}
                        </Box>
                      )}
                    >
                      {PLATFORM_OPTIONS.map((p) => (
                        <MenuItem key={p.value} value={p.value}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PlatformDot platform={p.value} />
                            {p.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                      {isUpdate
                        ? "Platform cannot be changed after creation"
                        : "One scheduler per feed + platform pair"}
                    </Typography>
                  </FormControl>
                </Grid>
              </Grid>
            </Section>

            {/* ── Days ── */}
            <Section
              icon={<CalendarMonthIcon sx={{ fontSize: 16 }} />}
              title="Publishing Days"
              subtitle="Publishing is allowed only on the selected days — leave all unselected to allow every day"
              accent="#0d9488"
            >
              <Box display="flex" gap={0.75} flexWrap="wrap" mb={1.5}>
                {DAYS_OF_WEEK.map((day) => {
                  const selected = formData.selected_days.includes(day.value);
                  return (
                    <Tooltip key={day.value} title={day.label}>
                      <Chip
                        label={day.short}
                        clickable
                        color={selected ? "primary" : "default"}
                        variant={selected ? "filled" : "outlined"}
                        onClick={() => toggleDay(day.value)}
                        sx={{ fontWeight: 600, minWidth: 56 }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
              <Box display="flex" gap={1}>
                <Button size="small" variant="text" onClick={() => setDays([0, 1, 2, 3, 4, 5, 6])} sx={{ textTransform: "none" }}>
                  Every day
                </Button>
                <Button size="small" variant="text" onClick={() => setDays([0, 1, 2, 3, 4])} sx={{ textTransform: "none" }}>
                  Weekdays
                </Button>
                <Button size="small" variant="text" onClick={() => setDays([5, 6])} sx={{ textTransform: "none" }}>
                  Weekend
                </Button>
                <Button size="small" variant="text" color="inherit" onClick={() => setDays([])} sx={{ textTransform: "none" }}>
                  Clear
                </Button>
              </Box>
            </Section>

            {/* ── Time windows ── */}
            <Section
              icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
              title="Publishing Time Windows"
              subtitle="Videos publish only inside these UTC ranges — add a second window for two slots per day"
              accent="#b45309"
            >
              <Grid container spacing={2}>
                {/* Window 1 */}
                <Grid item xs={12} md={6}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, borderLeft: "4px solid #4338ca", borderRadius: 1.5, height: "100%" }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                      <WbSunnyIcon sx={{ fontSize: 16, color: "#4338ca" }} />
                      <Typography variant="subtitle2" fontWeight={700} color="#4338ca">
                        Window 1
                      </Typography>
                      <Chip label="Required" size="small" sx={{ height: 18, fontSize: 10 }} color="primary" variant="outlined" />
                      {win1Len && (
                        <Chip label={win1Len} size="small" sx={{ height: 18, fontSize: 10, ml: "auto" }} />
                      )}
                    </Box>
                    <Box display="flex" gap={1}>
                      <TimeDurationPicker
                        sx={{ flex: 1 }}
                        required
                        label="Start (UTC)"
                        value={formData.time_start}
                        onChange={handleTime("time_start")}
                      />
                      <TimeDurationPicker
                        sx={{ flex: 1 }}
                        ampm={false}
                        required
                        label="End (UTC)"
                        value={formData.time_end}
                        onChange={handleTime("time_end")}
                      />
                    </Box>
                  </Paper>
                </Grid>

                {/* Window 2 */}
                <Grid item xs={12} md={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderLeft: `4px solid ${hasSecondWindow ? "#0d9488" : "#cbd5e1"}`,
                      borderRadius: 1.5,
                      height: "100%",
                      bgcolor: hasSecondWindow ? alpha("#0d9488", 0.03) : "transparent",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                      <DarkModeIcon sx={{ fontSize: 16, color: hasSecondWindow ? "#0d9488" : "text.disabled" }} />
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        color={hasSecondWindow ? "#0d9488" : "text.secondary"}
                      >
                        Window 2
                      </Typography>
                      <Chip label="Optional" size="small" sx={{ height: 18, fontSize: 10 }} variant="outlined" />
                      {win2Len && (
                        <Chip label={win2Len} size="small" sx={{ height: 18, fontSize: 10 }} color="success" variant="outlined" />
                      )}
                      {hasSecondWindow && (
                        <Tooltip title="Remove the second window">
                          <Button
                            size="small"
                            color="inherit"
                            startIcon={<ClearIcon sx={{ fontSize: "14px !important" }} />}
                            onClick={clearSecondWindow}
                            sx={{ ml: "auto", textTransform: "none", fontSize: 11, py: 0, minWidth: 0 }}
                          >
                            Clear
                          </Button>
                        </Tooltip>
                      )}
                    </Box>
                    <Box display="flex" gap={1}>
                      <TimeDurationPicker
                        sx={{ flex: 1 }}
                        label="Start (UTC)"
                        value={formData.time_start_2}
                        onChange={handleTime("time_start_2")}
                      />
                      <TimeDurationPicker
                        sx={{ flex: 1 }}
                        ampm={false}
                        label="End (UTC)"
                        value={formData.time_end_2}
                        onChange={handleTime("time_end_2")}
                      />
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <PublicIcon sx={{ fontSize: 14 }} color="action" />
                    <Typography variant="caption" color="text.secondary">
                      {scheduleSummary || "All times are in UTC — set at least the first window."}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Section>

            {/* ── Limits ── */}
            <Section
              icon={<TuneIcon sx={{ fontSize: 16 }} />}
              title="Publishing Limits"
              subtitle="Optional pacing rules — both apply across all time windows combined"
              accent="#be185d"
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextInput
                    label="Uploading Gap"
                    type="number"
                    fullWidth
                    name="uploading_gap"
                    value={formData.uploading_gap}
                    onChange={handleChange}
                    helperText="Minimum minutes between two publishes on this platform (leave empty for no gap)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HourglassBottomIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: <InputAdornment position="end">min</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    label="Max Uploads Per Day"
                    type="number"
                    fullWidth
                    name="max_uploads_per_day"
                    value={formData.max_uploads_per_day}
                    onChange={handleChange}
                    helperText="Hard daily cap for this platform (leave empty for unlimited)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SpeedIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: <InputAdornment position="end">/ day</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
            </Section>
          </Box>
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
        <Button
          onClick={() => onClose(false)}
          variant="outlined"
          sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, borderColor: "#e2e8f0", color: "text.secondary" }}
        >
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          type="submit"
          form="social-scheduler-form"
          startIcon={<ScheduleIcon />}
          sx={{
            borderRadius: 1.5,
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            background: "linear-gradient(135deg, #312e81, #4338ca)",
            "&:hover": { background: "linear-gradient(135deg, #1e1b4b, #312e81)" },
          }}
        >
          {isUpdate ? "Update Scheduler" : "Create Scheduler"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
